import React from "react";
import { Link } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  requestAnonymousReset,
  requestAnonymousResetSms,
  fetchResetOptions,
  type ResetOptions,
} from "@/lib/api";

// The backend answers the same generic 200 whether or not the identifier matches
// an account, so the UI renders ONE neutral confirmation per channel — it never
// reveals whether an account exists.
const NEUTRAL_EMAIL =
  "If an account exists for that email address, we've sent a link you can use " +
  "to reset your password. Please check your inbox and spam folder.";
const NEUTRAL_SMS =
  "If an account exists for that phone number, we've sent a text message with a " +
  "password-reset code. It may take a moment to arrive.";

type Channel = "email" | "sms";

/** A legal link when configured, else a clearly-marked placeholder (never an
 *  invented URL). */
function LegalLink({ label, url }: { label: string; url: string }) {
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 underline hover:opacity-80">
        {label}
      </a>
    );
  }
  return <span className="opacity-60">{label} (link not configured)</span>;
}

export function ForgotPasswordPage() {
  const [options, setOptions] = React.useState<ResetOptions | null>(null);
  const [channel, setChannel] = React.useState<Channel>("email");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [consent, setConsent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  React.useEffect(() => {
    // Backend-authoritative: the SMS option only appears when the channel is on.
    fetchResetOptions()
      .then(setOptions)
      .catch(() =>
        setOptions({
          email_enabled: true,
          sms_enabled: false,
          sms_consent_text: "",
          sms_consent_version: "",
          privacy_policy_url: "",
          terms_of_service_url: "",
        }),
      );
  }, []);

  const smsOn = Boolean(options?.sms_enabled);
  const canSubmit =
    channel === "email" ? Boolean(email.trim()) : Boolean(phone.trim()) && consent;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading || !canSubmit) return;
    setLoading(true);
    try {
      if (channel === "email") await requestAnonymousReset(email.trim());
      else await requestAnonymousResetSms(phone.trim(), consent);
    } catch {
      // Deliberately ignored: surfacing a failure would leak signal and differ
      // from the success path. The confirmation is always the same.
    } finally {
      setLoading(false);
      setSent(true);
    }
  }

  const title = sent
    ? channel === "email"
      ? "Check your email"
      : "Check your phone"
    : "Reset your password";

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            {!sent && (
              <CardDescription>
                {smsOn
                  ? "Choose how you'd like to reset your password."
                  : "Enter the email address for your account and we'll send you a reset link."}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4">
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                  {channel === "email" ? NEUTRAL_EMAIL : NEUTRAL_SMS}
                </p>
                <Button asChild className="w-full" variant="outline">
                  <Link to="/login">Back to sign in</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                {smsOn && (
                  <div className="flex rounded-lg border p-1 text-sm">
                    <button
                      type="button"
                      onClick={() => setChannel("email")}
                      className={`flex-1 rounded-md py-1.5 ${channel === "email" ? "bg-zinc-200 font-medium dark:bg-zinc-700" : ""}`}
                    >
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => setChannel("sms")}
                      className={`flex-1 rounded-md py-1.5 ${channel === "sms" ? "bg-zinc-200 font-medium dark:bg-zinc-700" : ""}`}
                    >
                      Text message
                    </button>
                  </div>
                )}

                {channel === "email" ? (
                  <div>
                    <Label>Email</Label>
                    <Input
                      className="mt-2"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoFocus
                      autoComplete="email"
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Label>Mobile number</Label>
                      <Input
                        className="mt-2"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        autoFocus
                        autoComplete="tel"
                        placeholder="+1 415 555 0142"
                      />
                    </div>
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                      />
                      <span className="text-xs leading-relaxed text-zinc-600 whitespace-pre-line dark:text-zinc-300">
                        {options?.sms_consent_text}
                      </span>
                    </label>
                    <div className="text-center text-xs">
                      <LegalLink label="Privacy Policy" url={options?.privacy_policy_url || ""} />
                      <span className="px-2 opacity-40">|</span>
                      <LegalLink label="Terms of Service" url={options?.terms_of_service_url || ""} />
                    </div>
                  </div>
                )}

                <Button className="w-full" type="submit" disabled={loading || !canSubmit}>
                  {loading
                    ? "Sending…"
                    : channel === "email"
                      ? "Send reset link"
                      : "Send code by text"}
                </Button>
                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                  >
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
